import {
  IGlobalActions,
  IInteractiveState,
  IMeta,
  IReduxAction,
} from '@bees-web/core/types'
import { MouseEvent } from 'react'
import { AnyAction } from 'redux'
import { ThunkDispatch } from 'redux-thunk'
import {
  RATE_MY_SERVICE_MODAL_CHANGE,
  RATE_MY_SERVICE_QUESTIONS,
  RATE_MY_SERVICE_RATED,
  RATE_MY_SERVICE_REDIRECT_URL,
  RATE_MY_SERVICE_SUBMISSION_COMPLETED,
  RATE_MY_SERVICE_SUBMISSION_FAILED,
} from '../../constants/ratings'
import { IRateMyService } from '../../interfaces/ratings'
import {
  defineEntryMethodAndScreen,
  getAnswersResponse,
  getAnswerTypes,
} from '../../utils/ratingsUtils'
import { trackServiceRatingSubmission } from './segment/RateMyService'

export const rateMyServiceChange = (payload: object): IReduxAction => ({
  type: RATE_MY_SERVICE_MODAL_CHANGE,
  payload,
})
export const rateMyServiceQuestionsChange = (
  payload: object
): IReduxAction => ({
  type: RATE_MY_SERVICE_QUESTIONS,
  payload,
})

export const rateMyServiceRedirectUrl = (url: object): IReduxAction => ({
  type: RATE_MY_SERVICE_REDIRECT_URL,
  payload: url,
})

export const rateMyServiceRated = (ticketNumber: string): IReduxAction => ({
  type: RATE_MY_SERVICE_RATED,
  payload: {
    ticketNumber,
  },
})

export const rateMyServiceResetState = rateMyServiceChange({
  isShowing: false,
  comment: '',
  score: 0,
  skipped: false,
  answers: [{ questionId: '', responses: [] }],
  modalStatus: 'initial',
  loadingFeedBack: false,
  ratingThreshold: null,
  survey: {},
  surveyError: false,
  submittedMessage: '',
})

export const rateMyServiceSubmitRating = (
  e: MouseEvent<HTMLButtonElement>,
  meta: IMeta,
  interactiveState: IInteractiveState<IRateMyService>,
  globalActions: IGlobalActions,
  dispatch: ThunkDispatch<IInteractiveState<IRateMyService>, {}, AnyAction>,
  skip: boolean = false
) => {
  const { currentTarget } = e
  currentTarget.setAttribute('disabled', 'true')
  const { vendorId, ticketId } = meta
  const { accountId } = interactiveState.globals.authentication.selectedAccount
  let { score, comment, skipped, answers } =
    interactiveState.state.ratingService.rateMyService
  const { authentication, user } = interactiveState.globals || null
  const rmsResponseState =
    interactiveState.state.ratingService.rateMyService.rmsResponse

  answers = answers?.filter(
    ({ questionId, responses }) => questionId && responses.length > 0
  )
  if (skip) {
    score = 0
    comment = ''
    skipped = true
    answers = []
  }
  dispatch(
    globalActions.requester.doRequest({
      ...meta.request,
      body: {
        ...meta.request.body,
        vendorId,
        score,
        comment,
        skipped,
        answers,
        accountId,
      },
      onError: async ({ response }) => {
        if (skip === false) {
          dispatch(rateMyServiceChange({ modalStatus: 'error' }))
          const failure_reason = (await response.json()).message
          const { entry_method, screen_name } = defineEntryMethodAndScreen()
          dispatch(
            trackServiceRatingSubmission({
              type: RATE_MY_SERVICE_SUBMISSION_FAILED,
              app_instance: user.country,
              failure_reason,
              poc_id: authentication.selectedAccount.accountId,
              rating_free_text: comment,
              rating_given: score,
              screen_name: screen_name,
              vendorId,
              ticket_id: ticketId,
              rating_type: rmsResponseState.ratingType,
              answer_type: getAnswerTypes(rmsResponseState.questions),
              number_of_questions: rmsResponseState.questions.length,
              question_responses: getAnswersResponse(answers),
              entry_method: entry_method,
            })
          )
        }
      },
      finally: ({ response }) => {
        if (skip === false && response.ok) {
          dispatch(rateMyServiceChange({ modalStatus: 'submitted' }))
          const { entry_method, screen_name } = defineEntryMethodAndScreen()
          dispatch(
            trackServiceRatingSubmission({
              type: RATE_MY_SERVICE_SUBMISSION_COMPLETED,
              poc_id: authentication.selectedAccount.accountId,
              app_instance: user.country,
              answer_type: getAnswerTypes(rmsResponseState.questions),
              number_of_questions: rmsResponseState.questions.length,
              question_responses: getAnswersResponse(answers),
              rating_free_text: comment,
              rating_given: score,
              rating_type: rmsResponseState.ratingType,
              screen_name: screen_name,
              ticket_id: ticketId,
              vendorId,
              entry_method: entry_method,
            })
          )
        }
      },
    })
  )
  dispatch(rateMyServiceRated(ticketId))
}

export const rateMyServiceSubmitDismiss = (
  e: MouseEvent<HTMLButtonElement>,
  meta: IMeta,
  interactiveState: IInteractiveState<IRateMyService>,
  globalActions: IGlobalActions,
  dispatch: ThunkDispatch<IInteractiveState<IRateMyService>, {}, AnyAction>
) => {
  if (
    interactiveState.state.ratingService.rateMyService.modalStatus === 'initial'
  ) {
    rateMyServiceSubmitRating(
      e,
      meta,
      interactiveState,
      globalActions,
      dispatch,
      true
    )
  }
  dispatch(rateMyServiceResetState)
}
